import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Store para trackear las páginas más visitadas del usuario
 */
const usePageVisitsStore = create(
  persist(
    (set, get) => ({
      pageVisits: {}, // { '/path': { count: number, title: string, lastVisited: timestamp } }

      /**
       * Registra una visita a una página
       * @param {string} path - Ruta de la página
       * @param {string} title - Título de la página
       */
      visitPage: (path, title) => {
        const now = Date.now();
        set((state) => ({
          pageVisits: {
            ...state.pageVisits,
            [path]: {
              count: (state.pageVisits[path]?.count || 0) + 1,
              title: title,
              lastVisited: now,
              path: path,
            },
          },
        }));
      },

      /**
       * Obtiene las páginas más visitadas ordenadas por número de visitas
       * @param {number} limit - Número máximo de páginas a retornar
       * @returns {Array} Array de páginas ordenadas por visitas
       */
      getMostVisitedPages: (limit = 12) => {
        const { pageVisits } = get();
        return Object.values(pageVisits)
          .filter((page) => page.count > 0)
          .sort((a, b) => {
            if (b.count !== a.count) {
              return b.count - a.count;
            }
            return b.lastVisited - a.lastVisited;
          })
          .slice(0, limit);
      },

      /**
       * Obtiene el número total de páginas únicas visitadas
       * @returns {number} Número de páginas únicas visitadas
       */
      getTotalPagesVisited: () => {
        const { pageVisits } = get();
        return Object.keys(pageVisits).length;
      },

      /**
       * Limpia todas las estadísticas de visitas
       */
      clearAllVisits: () => {
        set({ pageVisits: {} });
      },

      /**
       * Obtiene las estadísticas de una página específica
       * @param {string} path - Ruta de la página
       * @returns {Object|null} Estadísticas de la página o null si no existe
       */
      getPageStats: (path) => {
        const { pageVisits } = get();
        return pageVisits[path] || null;
      },

      /**
       * Verifica si hay suficientes datos de visitas para mostrar páginas ordenadas
       * @param {number} minVisits - Número mínimo de páginas visitadas
       * @returns {boolean} True si hay suficientes datos
       */
      hasSufficientData: (minVisits = 3) => {
        const { pageVisits } = get();
        const pagesWithMultipleVisits = Object.values(pageVisits).filter(
          (page) => page.count >= 2
        ).length;
        return pagesWithMultipleVisits >= minVisits;
      },
    }),
    {
      name: 'page-visits-storage',
      version: 1,
    }
  )
);

export default usePageVisitsStore;
